/**
 * Iter 197 · Brand GTM Playbook tab.
 *
 * A brand can own multiple named GTM playbooks (e.g. "India Launch 2026",
 * "US Expansion"). Each playbook holds two authored sections — ICP &
 * Personas + Positioning House — that ship in Iter 197. Every section
 * supports:
 *   • AI-generated first drafts via Claude Sonnet 4.5
 *   • Manual editing that captures a revision entry (with diff, author,
 *     source tag)
 *   • Version history in a right-hand drawer
 *
 * Playbooks can be seeded from 12 built-in industry templates OR from a
 * custom-generated template. A shareable public briefing link exposes a
 * read-only view at /gtm/share/:token.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Rocket, Plus, Sparkles, Wand2, Copy, Trash2, Share2, Users, Target,
  History, ExternalLink, Loader2, CheckCircle2, Layers, X, ArrowRight,
  GitBranch, Megaphone, FileText, RefreshCw, Calendar, Zap, Lightbulb,
  Link as LinkIcon, Package as PackageIcon, ShieldCheck, Star,
} from "lucide-react";
import { fmtRelative } from "@/lib/format";

const SECTIONS = [
  { key: "icp_personas", label: "ICP & Personas", icon: Users },
  { key: "positioning_house", label: "Positioning House", icon: Target },
  { key: "offer_pricing", label: "Offer & Pricing", icon: PackageIcon },
  { key: "sales_enablement", label: "Sales Enablement", icon: ShieldCheck },
  { key: "funnel_channel_map", label: "Funnel & KPI", icon: GitBranch },
  { key: "launch_calendar", label: "Launch Calendar", icon: Calendar },
];

export default function BrandGtmTab({ brand }) {
  const { activeWorkspaceId: wsId } = useAuth();
  const bid = brand?.id;
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => api
    .get(`/workspaces/${wsId}/brands/${bid}/gtm/playbooks`)
    .then(r => setRows(r.data.rows || []))
    .catch(() => setRows([]));

  useEffect(() => { if (wsId && bid) load(); /* eslint-disable-next-line */ }, [wsId, bid]);

  if (openId) {
    return (
      <PlaybookEditor wsId={wsId} bid={bid} pid={openId}
                             onBack={() => { setOpenId(null); load(); }} />
    );
  }

  return (
    <div className="space-y-4" data-testid="brand-gtm-tab">
      <div className="flex items-center gap-2">
        <Rocket className="w-4 h-4 text-primary" />
        <h2 className="text-xl font-bold">GTM Playbooks</h2>
        <Badge variant="outline" className="rounded-none text-[10px]" data-testid="gtm-count">
          {rows === null ? "…" : `${rows.length} playbook${rows.length === 1 ? "" : "s"}`}
        </Badge>
        <div className="ml-auto flex items-center gap-2">
          <Button className="rounded-none" onClick={() => setCreating(true)}
                    data-testid="gtm-new-playbook">
            <Plus className="w-4 h-4 mr-1" /> New playbook
          </Button>
        </div>
      </div>
      <p className="text-xs text-foreground/60">
        Land the strategy layer for this brand. Each playbook is a versioned document —
        ICP + Positioning today; funnel, launch calendar, and enablement in
        upcoming iterations. Seed from a template or a custom prompt.
      </p>

      {rows === null && (
        <div className="border border-border p-6 text-sm text-foreground/60">Loading playbooks…</div>
      )}
      {rows && rows.length === 0 && (
        <div className="border border-dashed border-border p-10 text-center" data-testid="gtm-empty">
          <Rocket className="w-8 h-8 mx-auto text-primary mb-2" />
          <div className="font-semibold">No GTM playbooks yet for this brand.</div>
          <p className="text-xs text-foreground/60 mt-1">
            Start from a curated industry template — or describe your industry and let Claude generate a custom one.
          </p>
          <Button className="rounded-none mt-4" onClick={() => setCreating(true)}
                    data-testid="gtm-empty-start">
            <Plus className="w-4 h-4 mr-1" /> Create your first playbook
          </Button>
        </div>
      )}

      {rows && rows.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map(p => (
            <PlaybookCard key={p.id} p={p} wsId={wsId} bid={bid}
                                  onOpen={() => setOpenId(p.id)} onReload={load} />
          ))}
        </div>
      )}

      {creating && (
        <NewPlaybookDialog wsId={wsId} bid={bid}
                                   onClose={() => setCreating(false)}
                                   onCreated={(p) => { setCreating(false); load(); setOpenId(p.id); }} />
      )}
    </div>
  );
}

// ─────────────── Playbook card ───────────────
function PlaybookCard({ p, wsId, bid, onOpen, onReload }) {
  const activeTone = p.status === "active"
    ? "border-emerald-400 bg-emerald-50 text-emerald-800"
    : p.status === "archived"
      ? "border-border bg-muted/40 text-muted-foreground"
      : "border-amber-400 bg-amber-50 text-amber-800";
  const del = async () => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    await api.delete(`/workspaces/${wsId}/brands/${bid}/gtm/playbooks/${p.id}`);
    toast.success("Deleted");
    onReload();
  };
  const dup = async () => {
    await api.post(`/workspaces/${wsId}/brands/${bid}/gtm/playbooks/${p.id}/duplicate`);
    toast.success("Duplicated");
    onReload();
  };
  const setActive = async () => {
    await api.post(`/workspaces/${wsId}/brands/${bid}/gtm/playbooks/${p.id}/set-active`);
    toast.success("Marked active");
    onReload();
  };
  return (
    <div className="border border-border bg-card p-4 space-y-3 hover:border-primary transition-colors"
            data-testid={`gtm-playbook-card-${p.id}`}>
      <div className="flex items-start gap-2">
        <Rocket className="w-4 h-4 text-primary shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{p.name}</div>
          <div className="text-[11px] text-muted-foreground truncate">{p.industry || "—"}</div>
        </div>
        <span className={`text-[9px] font-semibold uppercase tracking-widest border px-1.5 py-0.5 ${activeTone}`}>
          {p.status}
        </span>
      </div>
      <div className="text-[11px] text-muted-foreground">
        Updated {fmtRelative(p.updated_at)} · by {p.created_by_name || "—"}
      </div>
      <div className="flex items-center gap-1">
        <Button size="sm" className="rounded-none h-7" onClick={onOpen}
                  data-testid={`gtm-open-${p.id}`}>
          Open <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
        {p.status !== "active" && (
          <Button size="sm" variant="outline" className="rounded-none h-7"
                    onClick={setActive}
                    data-testid={`gtm-activate-${p.id}`}>
            Set active
          </Button>
        )}
        <Button size="sm" variant="ghost" className="rounded-none h-7 px-2"
                  onClick={dup} title="Duplicate"
                  data-testid={`gtm-duplicate-${p.id}`}>
          <Copy className="w-3 h-3" />
        </Button>
        <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-destructive"
                  onClick={del} title="Delete"
                  data-testid={`gtm-delete-${p.id}`}>
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}

// ─────────────── New playbook dialog ───────────────
function NewPlaybookDialog({ wsId, bid, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [step, setStep] = useState("name");  // name → template
  const [templates, setTemplates] = useState([]);
  const [customPrompt, setCustomPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [gen, setGen] = useState(null); // custom-generated template preview

  useEffect(() => {
    api.get(`/workspaces/${wsId}/gtm/industry-templates`)
      .then(r => setTemplates(r.data?.built_in || []))
      .catch(() => setTemplates([]));
  }, [wsId]);

  const create = async (templateId) => {
    if (!name.trim()) { toast.error("Give the playbook a name first"); return; }
    setBusy(true);
    try {
      const body = { name: name.trim(), status: "draft" };
      if (templateId) body.template_id = templateId;
      const r = await api.post(`/workspaces/${wsId}/brands/${bid}/gtm/playbooks`, body);
      toast.success("Playbook created");
      onCreated(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to create")); }
    finally { setBusy(false); }
  };

  const createAndApplyCustom = async () => {
    if (!name.trim()) { toast.error("Give the playbook a name first"); return; }
    if (!gen) return;
    setBusy(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/brands/${bid}/gtm/playbooks`,
        { name: name.trim(), industry: gen.industry, status: "draft" });
      await api.post(
        `/workspaces/${wsId}/brands/${bid}/gtm/playbooks/${r.data.id}/apply-template`,
        { template: gen, overwrite: true });
      toast.success("Playbook created from custom template");
      onCreated(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };

  const generateCustom = async () => {
    if (customPrompt.trim().length < 6) { toast.error("Add a few more words to describe the industry"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/gtm/industry-templates/generate-custom`,
        { prompt: customPrompt.trim() });
      setGen(r.data);
      toast.success("Custom template ready — review & apply");
    } catch (e) { toast.error(apiErrorMessage(e, "AI generation failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="gtm-new-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Rocket className="w-4 h-4 text-primary" /> New GTM Playbook
          </DialogTitle>
        </DialogHeader>

        {step === "name" && (
          <div className="space-y-3">
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Playbook name *</Label>
              <Input value={name} onChange={e => setName(e.target.value)}
                       placeholder="e.g. India Launch 2026 · North Zone"
                       className="rounded-none" data-testid="gtm-new-name" />
            </div>
            <div className="border border-emerald-300 bg-emerald-50/50 p-3 text-xs text-emerald-900">
              Next, seed the playbook from a curated <b>industry template</b> — or
              describe your industry and let Claude build a custom one. You can
              also skip and start blank.
            </div>
            <DialogFooter className="pt-2">
              <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
              <Button variant="outline" className="rounded-none"
                        onClick={() => create(null)} disabled={busy || !name.trim()}
                        data-testid="gtm-new-blank">
                Start blank
              </Button>
              <Button className="rounded-none" onClick={() => setStep("template")}
                        disabled={!name.trim()} data-testid="gtm-new-next">
                Pick a template <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "template" && (
          <div className="space-y-4">
            {/* Custom prompt section */}
            <div className="border border-primary/40 bg-primary/5 p-3 space-y-2">
              <div className="flex items-center gap-2">
                <Wand2 className="w-4 h-4 text-primary" />
                <div className="text-sm font-semibold">Generate a custom industry template</div>
              </div>
              <Textarea rows={2} value={customPrompt}
                            onChange={e => setCustomPrompt(e.target.value)}
                            placeholder="Describe your industry / archetype — e.g. 'Regenerative agriculture co-op selling to premium urban households'"
                            className="rounded-none text-xs"
                            data-testid="gtm-custom-prompt" />
              <div className="flex items-center gap-2">
                <Button size="sm" className="rounded-none" onClick={generateCustom}
                          disabled={busy || customPrompt.trim().length < 6}
                          data-testid="gtm-custom-generate">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                  Generate with Claude
                </Button>
                {gen && (
                  <>
                    <div className="text-xs">Generated: <b>{gen.name}</b> · {gen.industry}</div>
                    <Button size="sm" className="rounded-none ml-auto" onClick={createAndApplyCustom} disabled={busy}
                              data-testid="gtm-custom-apply">
                      Apply <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">
              Or pick a built-in industry template ({templates.length})
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-96 overflow-y-auto pr-1"
                    data-testid="gtm-template-list">
              {templates.map(t => (
                <button key={t.id} type="button"
                            onClick={() => create(t.id)}
                            disabled={busy}
                            className="text-left border border-border hover:border-primary bg-card p-3 space-y-1 disabled:opacity-50"
                            data-testid={`gtm-template-${t.id}`}>
                  <div className="flex items-center gap-2">
                    <Layers className="w-3.5 h-3.5 text-primary" />
                    <div className="text-sm font-semibold flex-1 truncate">{t.name}</div>
                    <span className="text-[9px] uppercase tracking-widest border border-border text-muted-foreground px-1">
                      {t.persona_count}p · {t.pillar_preview.length}pil
                    </span>
                  </div>
                  <div className="text-[11px] text-muted-foreground line-clamp-2">{t.tagline}</div>
                  <div className="flex flex-wrap gap-1">
                    {t.pillar_preview.map(pv => (
                      <span key={pv} className="text-[9px] uppercase tracking-widest border border-border bg-muted/30 px-1">
                        {pv}
                      </span>
                    ))}
                  </div>
                </button>
              ))}
            </div>
            <DialogFooter className="pt-2">
              <Button variant="outline" className="rounded-none" onClick={() => setStep("name")}>Back</Button>
              <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─────────────── Playbook editor ───────────────
function PlaybookEditor({ wsId, bid, pid, onBack }) {
  const [doc, setDoc] = useState(null);
  const [active, setActive] = useState("icp_personas");
  const [showRevisions, setShowRevisions] = useState(false);

  const load = () => api
    .get(`/workspaces/${wsId}/brands/${bid}/gtm/playbooks/${pid}`)
    .then(r => setDoc(r.data))
    .catch(e => toast.error(apiErrorMessage(e, "Failed to load")));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);

  if (!doc) {
    return <div className="p-6 text-sm text-foreground/60">Loading playbook…</div>;
  }

  return (
    <div className="flex flex-col h-[calc(100vh-16rem)] min-h-[520px]" data-testid="gtm-playbook-editor">
      <div className="flex items-center gap-2 border-b border-border pb-3 shrink-0">
        <Button variant="ghost" size="sm" className="rounded-none h-7"
                  onClick={onBack} data-testid="gtm-editor-back">
          ← Back
        </Button>
        <Rocket className="w-4 h-4 text-primary" />
        <div className="flex-1">
          <div className="font-bold text-lg truncate">{doc.name}</div>
          <div className="text-[11px] text-muted-foreground">{doc.industry || "—"} · {doc.status}</div>
        </div>
        <Button variant="outline" size="sm" className="rounded-none h-7"
                  onClick={() => setShowRevisions(true)}
                  data-testid="gtm-editor-history">
          <History className="w-3.5 h-3.5 mr-1" /> History
        </Button>
        <ShareControls wsId={wsId} bid={bid} pid={pid} doc={doc} onReload={load} />
      </div>

      <div className="flex items-center gap-1 border-b border-border shrink-0">
        {SECTIONS.map(s => {
          const Icon = s.icon;
          const on = active === s.key;
          return (
            <button key={s.key} type="button" onClick={() => setActive(s.key)}
                        className={`text-xs uppercase tracking-widest font-semibold h-9 px-3 border-b-2 ${on ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                        data-testid={`gtm-section-tab-${s.key}`}>
              <Icon className="w-3.5 h-3.5 inline mr-1" /> {s.label}
            </button>
          );
        })}
      </div>

      <div className="flex-1 overflow-y-auto pt-3 pr-1" data-testid="gtm-editor-scroll">
        {active === "icp_personas" && (
          <IcpPersonasEditor wsId={wsId} bid={bid} pid={pid}
                                     value={doc.sections?.icp_personas}
                                     onUpdated={load} />
        )}
        {active === "positioning_house" && (
          <PositioningEditor wsId={wsId} bid={bid} pid={pid}
                                     value={doc.sections?.positioning_house}
                                     onUpdated={load} />
        )}
        {active === "offer_pricing" && (
          <OfferPricingEditor wsId={wsId} bid={bid} pid={pid}
                                          value={doc.sections?.offer_pricing}
                                          onUpdated={load} />
        )}
        {active === "sales_enablement" && (
          <SalesEnablementEditor wsId={wsId} bid={bid} pid={pid}
                                                value={doc.sections?.sales_enablement}
                                                onUpdated={load} />
        )}
        {active === "funnel_channel_map" && (
          <FunnelEditor wsId={wsId} bid={bid} pid={pid} onUpdated={load} />
        )}
        {active === "launch_calendar" && (
          <LaunchCalendarEditor wsId={wsId} bid={bid} pid={pid} doc={doc} onUpdated={load} />
        )}
      </div>

      {showRevisions && (
        <RevisionsDrawer wsId={wsId} bid={bid} pid={pid}
                                 onClose={() => setShowRevisions(false)} />
      )}
    </div>
  );
}

// ─────────────── Share controls ───────────────
function ShareControls({ wsId, bid, pid, doc, onReload }) {
  const [busy, setBusy] = useState(false);
  const shareUrl = doc.share_token
    ? `${window.location.origin}/gtm/share/${doc.share_token}`
    : null;
  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/brands/${bid}/gtm/playbooks/${pid}/share`);
      const url = `${window.location.origin}/gtm/share/${r.data.share_token}`;
      await navigator.clipboard?.writeText(url).catch(() => {});
      toast.success("Share link copied");
      onReload();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    if (!window.confirm("Revoke public link?")) return;
    setBusy(true);
    try {
      await api.delete(`/workspaces/${wsId}/brands/${bid}/gtm/playbooks/${pid}/share`);
      toast.success("Link revoked");
      onReload();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };
  if (!doc.share_token) {
    return (
      <Button variant="outline" size="sm" className="rounded-none h-7"
                onClick={create} disabled={busy}
                data-testid="gtm-share-create">
        <Share2 className="w-3.5 h-3.5 mr-1" /> Share
      </Button>
    );
  }
  return (
    <div className="inline-flex items-center gap-1" data-testid="gtm-share-active">
      <a href={shareUrl} target="_blank" rel="noopener noreferrer"
          className="text-[10px] uppercase tracking-widest font-semibold text-primary hover:underline inline-flex items-center gap-1 h-7 px-2 border border-primary bg-primary/5"
          data-testid="gtm-share-open">
        <ExternalLink className="w-3 h-3" /> Public link
      </a>
      <Button variant="ghost" size="sm" className="rounded-none h-7 px-2"
                onClick={() => { navigator.clipboard?.writeText(shareUrl); toast.success("Copied"); }}
                data-testid="gtm-share-copy">
        <Copy className="w-3 h-3" />
      </Button>
      <Button variant="ghost" size="sm" className="rounded-none h-7 px-2 text-destructive"
                onClick={revoke} disabled={busy}
                data-testid="gtm-share-revoke">
        <X className="w-3 h-3" />
      </Button>
    </div>
  );
}

// ─────────────── Section: ICP & Personas ───────────────
function IcpPersonasEditor({ wsId, bid, pid, value, onUpdated }) {
  const [draft, setDraft] = useState(value || { icp: {}, personas: [] });
  const [busy, setBusy] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  useEffect(() => { setDraft(value || { icp: {}, personas: [] }); }, [value]);
  const icp = draft.icp || {};
  const personas = draft.personas || [];

  const setIcp = (k, v) => setDraft(d => ({ ...d, icp: { ...(d.icp || {}), [k]: v } }));
  const setList = (k, v) => setIcp(k, v.split("\n").map(x => x.trim()).filter(Boolean));
  const setPersona = (idx, k, v) => setDraft(d => ({
    ...d, personas: d.personas.map((p, i) => i === idx ? { ...p, [k]: v } : p)
  }));
  const addPersona = () => setDraft(d => ({
    ...d, personas: [...(d.personas || []), { name: "", jtbd: "", pains: [], gains: [], watering_holes: [] }]
  }));
  const rmPersona = (idx) => setDraft(d => ({
    ...d, personas: d.personas.filter((_, i) => i !== idx)
  }));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/workspaces/${wsId}/brands/${bid}/gtm/playbooks/${pid}/sections/icp_personas`,
        { value: draft });
      toast.success("Saved · revision recorded");
      onUpdated();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const aiGenerate = async () => {
    setBusy(true);
    try {
      const r = await api.post(
        `/workspaces/${wsId}/brands/${bid}/gtm/playbooks/${pid}/sections/icp_personas/ai-generate`,
        { prompt_extra: aiPrompt.trim() || null });
      setDraft(r.data.value);
      toast.success("Claude drafted ICP & personas — edit and save");
      onUpdated();
    } catch (e) { toast.error(apiErrorMessage(e, "AI failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="gtm-section-icp-personas">
      <AiBar prompt={aiPrompt} setPrompt={setAiPrompt} onRun={aiGenerate}
                busy={busy} placeholder="Optional extra direction — e.g. 'Focus on India Tier-1 mid-market SaaS'"
                testid="icp" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-border bg-card p-4 space-y-3">
          <div className="text-sm font-semibold flex items-center gap-1">
            <Users className="w-3.5 h-3.5 text-primary" /> Ideal Customer Profile
          </div>
          <TextField label="Firmographic / one-liner" value={icp.firmographic || ""}
                        onChange={v => setIcp("firmographic", v)} testid="icp-firmo" />
          <ListField label="Trigger events (one per line)"
                        value={(icp.trigger_events || []).join("\n")}
                        onChange={v => setList("trigger_events", v)}
                        testid="icp-triggers" />
          <ListField label="Buying committee"
                        value={(icp.buying_committee || []).join("\n")}
                        onChange={v => setList("buying_committee", v)}
                        testid="icp-committee" />
          <ListField label="Disqualifiers"
                        value={(icp.disqualifiers || []).join("\n")}
                        onChange={v => setList("disqualifiers", v)}
                        testid="icp-disqualifiers" />
        </div>
        <div className="space-y-3">
          {personas.map((p, idx) => (
            <div key={idx} className="border border-border bg-card p-4 space-y-2"
                    data-testid={`gtm-persona-${idx}`}>
              <div className="flex items-center gap-2">
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                  Persona {idx + 1}
                </div>
                <Button variant="ghost" size="sm" className="rounded-none h-6 px-1 ml-auto text-destructive"
                          onClick={() => rmPersona(idx)}
                          data-testid={`gtm-persona-remove-${idx}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
              <TextField label="Name & role" value={p.name || ""}
                            onChange={v => setPersona(idx, "name", v)}
                            testid={`persona-${idx}-name`} />
              <TextField label="Job-to-be-done" value={p.jtbd || ""}
                            onChange={v => setPersona(idx, "jtbd", v)}
                            testid={`persona-${idx}-jtbd`} />
              <ListField label="Pains" value={(p.pains || []).join("\n")}
                            onChange={v => setPersona(idx, "pains", v.split("\n").map(x => x.trim()).filter(Boolean))}
                            testid={`persona-${idx}-pains`} />
              <ListField label="Gains" value={(p.gains || []).join("\n")}
                            onChange={v => setPersona(idx, "gains", v.split("\n").map(x => x.trim()).filter(Boolean))}
                            testid={`persona-${idx}-gains`} />
              <ListField label="Watering holes"
                            value={(p.watering_holes || []).join("\n")}
                            onChange={v => setPersona(idx, "watering_holes", v.split("\n").map(x => x.trim()).filter(Boolean))}
                            testid={`persona-${idx}-holes`} />
            </div>
          ))}
          <Button variant="outline" size="sm" className="rounded-none w-full"
                    onClick={addPersona} data-testid="gtm-persona-add">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add persona
          </Button>
        </div>
      </div>
      <SaveBar onSave={save} busy={busy} testid="icp" />
    </div>
  );
}

// ─────────────── Section: Positioning House ───────────────
function PositioningEditor({ wsId, bid, pid, value, onUpdated }) {
  const [draft, setDraft] = useState(value || { pillars: [], competitor_slaying: [] });
  const [aiPrompt, setAiPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDraft(value || { pillars: [], competitor_slaying: [] }); }, [value]);

  const setPillar = (idx, k, v) => setDraft(d => ({
    ...d, pillars: d.pillars.map((p, i) => i === idx ? { ...p, [k]: v } : p)
  }));
  const setPillarList = (idx, k, v) => setPillar(idx, k, v.split("\n").map(x => x.trim()).filter(Boolean));
  const addPillar = () => setDraft(d => ({
    ...d, pillars: [...(d.pillars || []), { title: "", sub: "", proof: [], rtbs: [] }]
  }));
  const rmPillar = (idx) => setDraft(d => ({ ...d, pillars: d.pillars.filter((_, i) => i !== idx) }));
  const setSlay = (idx, k, v) => setDraft(d => ({
    ...d, competitor_slaying: (d.competitor_slaying || []).map((c, i) => i === idx ? { ...c, [k]: v } : c)
  }));
  const addSlay = () => setDraft(d => ({
    ...d, competitor_slaying: [...(d.competitor_slaying || []), { competitor: "", why_we_win: "" }]
  }));
  const rmSlay = (idx) => setDraft(d => ({
    ...d, competitor_slaying: (d.competitor_slaying || []).filter((_, i) => i !== idx)
  }));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(
        `/workspaces/${wsId}/brands/${bid}/gtm/playbooks/${pid}/sections/positioning_house`,
        { value: draft });
      toast.success("Saved · revision recorded");
      onUpdated();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const aiGenerate = async () => {
    setBusy(true);
    try {
      const r = await api.post(
        `/workspaces/${wsId}/brands/${bid}/gtm/playbooks/${pid}/sections/positioning_house/ai-generate`,
        { prompt_extra: aiPrompt.trim() || null });
      setDraft(r.data.value);
      toast.success("Positioning house drafted — edit and save");
      onUpdated();
    } catch (e) { toast.error(apiErrorMessage(e, "AI failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="gtm-section-positioning">
      <AiBar prompt={aiPrompt} setPrompt={setAiPrompt} onRun={aiGenerate} busy={busy}
                placeholder="Optional extra direction — e.g. 'Slay Salesforce Einstein specifically'"
                testid="pos" />
      <div className="border border-border bg-card p-4 space-y-3">
        <div className="text-sm font-semibold flex items-center gap-1">
          <Target className="w-3.5 h-3.5 text-primary" /> Category POV & Promise
        </div>
        <TextField label="Category POV" value={draft.category_pov || ""}
                      onChange={v => setDraft(d => ({ ...d, category_pov: v }))}
                      testid="pos-cpov" />
        <TextField label="Value promise" value={draft.value_promise || ""}
                      onChange={v => setDraft(d => ({ ...d, value_promise: v }))}
                      testid="pos-promise" />
      </div>

      <div className="space-y-3">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Pillars</div>
        {(draft.pillars || []).map((p, idx) => (
          <div key={idx} className="border border-border bg-card p-4 space-y-2"
                  data-testid={`gtm-pillar-${idx}`}>
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 border border-primary text-primary text-xs font-bold flex items-center justify-center">
                {idx + 1}
              </span>
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Pillar</div>
              <Button variant="ghost" size="sm" className="rounded-none h-6 px-1 ml-auto text-destructive"
                        onClick={() => rmPillar(idx)}
                        data-testid={`gtm-pillar-remove-${idx}`}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
            <TextField label="Title" value={p.title || ""}
                          onChange={v => setPillar(idx, "title", v)}
                          testid={`pillar-${idx}-title`} />
            <TextField label="Sub-line" value={p.sub || ""}
                          onChange={v => setPillar(idx, "sub", v)}
                          testid={`pillar-${idx}-sub`} />
            <ListField label="Proof (one per line)"
                          value={(p.proof || []).join("\n")}
                          onChange={v => setPillarList(idx, "proof", v)}
                          testid={`pillar-${idx}-proof`} />
            <ListField label="Reasons to believe" value={(p.rtbs || []).join("\n")}
                          onChange={v => setPillarList(idx, "rtbs", v)}
                          testid={`pillar-${idx}-rtbs`} />
          </div>
        ))}
        <Button variant="outline" size="sm" className="rounded-none w-full"
                  onClick={addPillar} data-testid="gtm-pillar-add">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add pillar
        </Button>
      </div>

      <div className="space-y-3">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Competitor slaying</div>
        {(draft.competitor_slaying || []).map((c, idx) => (
          <div key={idx} className="border border-border bg-card p-4 grid grid-cols-1 md:grid-cols-2 gap-2 items-start"
                  data-testid={`gtm-slay-${idx}`}>
            <TextField label="Competitor" value={c.competitor || ""}
                          onChange={v => setSlay(idx, "competitor", v)}
                          testid={`slay-${idx}-competitor`} />
            <div className="flex items-start gap-1">
              <div className="flex-1">
                <TextField label="Why we win" value={c.why_we_win || ""}
                              onChange={v => setSlay(idx, "why_we_win", v)}
                              testid={`slay-${idx}-why`} />
              </div>
              <Button variant="ghost" size="sm" className="rounded-none h-9 px-1 text-destructive"
                        onClick={() => rmSlay(idx)}
                        data-testid={`gtm-slay-remove-${idx}`}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        ))}
        <Button variant="outline" size="sm" className="rounded-none w-full"
                  onClick={addSlay} data-testid="gtm-slay-add">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add competitor take
        </Button>
      </div>

      <SaveBar onSave={save} busy={busy} testid="pos" />
    </div>
  );
}

// ─────────────── Section: Funnel & KPI ───────────────
const STAGES = [
  { key: "awareness",       label: "Awareness",       tone: "border-violet-400 bg-violet-50 text-violet-900",  chip: "bg-violet-500" },
  { key: "consideration", label: "Consideration", tone: "border-sky-400 bg-sky-50 text-sky-900",             chip: "bg-sky-500" },
  { key: "decision",        label: "Decision",        tone: "border-amber-400 bg-amber-50 text-amber-900",   chip: "bg-amber-500" },
  { key: "retention",      label: "Retention",      tone: "border-emerald-400 bg-emerald-50 text-emerald-900", chip: "bg-emerald-500" },
];

const KPI_FIELDS = {
  awareness:       [["reach", "Reach"], ["impressions", "Impressions"], ["cpm", "CPM"]],
  consideration: [["leads", "Leads"], ["mqls", "MQLs"], ["mql_rate_pct", "MQL rate %"]],
  decision:        [["sqls", "SQLs"], ["won", "Won"], ["avg_deal_size", "Avg deal size"], ["win_rate_pct", "Win rate %"], ["cac", "CAC"]],
  retention:      [["active_customers", "Active customers"], ["churn_rate_pct", "Churn %"], ["ltv", "LTV"], ["payback_months", "Payback (mo)"]],
};

function FunnelEditor({ wsId, bid, pid, onUpdated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [targetsDraft, setTargetsDraft] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/workspaces/${wsId}/brands/${bid}/gtm/playbooks/${pid}/funnel`);
      setData(r.data);
      setTargetsDraft(r.data.targets || {});
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load funnel")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId, bid, pid]);

  const attribute = async ({ campaign_id, content_id, stage }) => {
    setBusy(true);
    try {
      await api.patch(
        `/workspaces/${wsId}/brands/${bid}/gtm/playbooks/${pid}/funnel/attribution`,
        { campaign_id, content_id, stage });
      toast.success(stage ? `Moved to ${stage}` : "Attribution cleared");
      await load();
      onUpdated?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
    finally { setBusy(false); }
  };

  const saveTargets = async () => {
    setBusy(true);
    try {
      await api.patch(
        `/workspaces/${wsId}/brands/${bid}/gtm/playbooks/${pid}/funnel/targets`,
        { targets: targetsDraft });
      toast.success("KPI targets saved");
      await load();
      onUpdated?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  if (loading || !data) {
    return <div className="text-sm text-foreground/60 p-6" data-testid="gtm-funnel-loading">Loading funnel…</div>;
  }

  const totals = data.totals || {};

  return (
    <div className="space-y-4" data-testid="gtm-section-funnel">
      <div className="border border-primary/30 bg-primary/5 p-3 flex items-center gap-2 flex-wrap">
        <GitBranch className="w-4 h-4 text-primary" />
        <div className="text-xs text-foreground/80 flex-1 min-w-[240px]">
          <b>Funnel canvas.</b> Every marketing campaign + brand content item
          is auto-attributed to a stage. Click <b>Move</b> on any card to
          override.
        </div>
        <ApplyToMarketingButton wsId={wsId} bid={bid} pid={pid} />
        <ExplainFunnelButton wsId={wsId} bid={bid} pid={pid} />
        <Button size="sm" variant="ghost" className="rounded-none h-7"
                  onClick={load} disabled={busy}
                  data-testid="gtm-funnel-refresh">
          <RefreshCw className={`w-3 h-3 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* KPI totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {[
          ["Campaigns", totals.campaigns_total],
          ["Content items", totals.content_total],
          ["Sent", totals.sent_total],
          ["Opened", totals.opened_total],
          ["Clicked", totals.clicked_total],
        ].map(([l, v]) => (
          <div key={l} className="border border-border bg-card p-2" data-testid={`gtm-funnel-total-${l.toLowerCase().replace(/ /g,'-')}`}>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{l}</div>
            <div className="text-lg font-mono font-black">{v ?? 0}</div>
          </div>
        ))}
      </div>

      {/* 4-stage canvas */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {STAGES.map((st, idx) => {
          const stage = (data.stages || []).find(x => x.stage === st.key) || {campaigns: [], content: [], actuals: {}};
          return (
            <div key={st.key} className={`border-2 ${st.tone.split(" ")[0]} bg-card`}
                    data-testid={`gtm-stage-${st.key}`}>
              <div className={`flex items-center gap-2 p-2 border-b border-border ${st.tone}`}>
                <span className={`w-5 h-5 rounded-full ${st.chip} text-white text-[10px] font-bold flex items-center justify-center`}>{idx + 1}</span>
                <div className="text-sm font-bold uppercase tracking-widest">{st.label}</div>
                <div className="ml-auto text-[10px] font-mono">
                  {stage.campaigns.length}📢 · {stage.content.length}📄
                </div>
              </div>
              {/* Campaigns */}
              {stage.campaigns.length > 0 && (
                <div className="p-2 space-y-1 border-b border-border">
                  <div className="text-[9px] uppercase tracking-widest font-semibold text-muted-foreground flex items-center gap-1">
                    <Megaphone className="w-3 h-3" /> Campaigns
                  </div>
                  {stage.campaigns.slice(0, 8).map(c => (
                    <StageCard key={c.id} item={c} kind="campaign"
                                    currentStage={st.key} busy={busy}
                                    onMove={(stage) => attribute({ campaign_id: c.id, stage })} />
                  ))}
                  {stage.campaigns.length > 8 && (
                    <div className="text-[10px] text-muted-foreground italic">+ {stage.campaigns.length - 8} more</div>
                  )}
                </div>
              )}
              {/* Content */}
              {stage.content.length > 0 && (
                <div className="p-2 space-y-1">
                  <div className="text-[9px] uppercase tracking-widest font-semibold text-muted-foreground flex items-center gap-1">
                    <FileText className="w-3 h-3" /> Content
                  </div>
                  {stage.content.slice(0, 8).map(c => (
                    <StageCard key={c.id} item={c} kind="content"
                                    currentStage={st.key} busy={busy}
                                    onMove={(stage) => attribute({ content_id: c.id, stage })} />
                  ))}
                  {stage.content.length > 8 && (
                    <div className="text-[10px] text-muted-foreground italic">+ {stage.content.length - 8} more</div>
                  )}
                </div>
              )}
              {stage.campaigns.length === 0 && stage.content.length === 0 && (
                <div className="text-[11px] text-muted-foreground italic p-4 text-center">
                  Nothing attributed to {st.label.toLowerCase()} yet.
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* KPI seed board */}
      <div className="border border-border bg-card p-3 space-y-3">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-primary" />
          <div className="text-sm font-semibold">KPI seed board · forecast targets</div>
          <div className="ml-auto text-[11px] text-muted-foreground">Actuals hydrate from campaign stats.</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {STAGES.map(st => (
            <div key={st.key} className={`border ${st.tone.split(" ")[0]} p-3`} data-testid={`gtm-kpi-${st.key}`}>
              <div className={`text-[10px] uppercase tracking-widest font-semibold mb-2 ${st.tone}`}>{st.label}</div>
              <div className="grid grid-cols-2 gap-2">
                {(KPI_FIELDS[st.key] || []).map(([k, label]) => (
                  <div key={k}>
                    <Label className="label-eyebrow mb-1 inline-block">{label}</Label>
                    <Input type="number" step="any"
                              value={(targetsDraft?.[st.key] || {})[k] ?? 0}
                              onChange={e => setTargetsDraft(d => ({
                                ...(d || {}),
                                [st.key]: { ...((d || {})[st.key] || {}), [k]: Number(e.target.value) || 0 }
                              }))}
                              className="h-8 rounded-none text-sm"
                              data-testid={`gtm-kpi-input-${st.key}-${k}`} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="sticky bottom-0 z-10 bg-background/95 backdrop-blur border-t border-border pt-3 flex items-center justify-end">
          <Button className="rounded-none" onClick={saveTargets} disabled={busy}
                    data-testid="gtm-kpi-save">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
            Save KPI targets
          </Button>
        </div>
      </div>
    </div>
  );
}

function StageCard({ item, kind, currentStage, busy, onMove }) {
  const [movingOpen, setMovingOpen] = useState(false);
  const title = kind === "campaign"
    ? item.name
    : `${item.format || "?"} · ${item.hook || ""}`;
  const meta = kind === "campaign"
    ? `${item.channel || "—"} · ${item.status || "draft"} · ${item.sent || 0} sent`
    : `${item.status || "draft"}${item.pillar ? ` · ${item.pillar}` : ""}`;
  return (
    <div className="border border-border bg-background p-1.5 text-[11px]"
            data-testid={`gtm-stage-card-${kind}-${item.id}`}>
      <div className="flex items-start gap-1">
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{title || "(untitled)"}</div>
          <div className="text-[10px] text-muted-foreground truncate">{meta}</div>
        </div>
        {item.overridden ? (
          <span className="text-[8px] uppercase font-bold tracking-widest bg-primary text-primary-foreground px-1"
                   title={`Auto-classified as ${item.auto_stage}`}>OVR</span>
        ) : (
          <span className="text-[8px] uppercase font-bold tracking-widest border border-border text-muted-foreground px-1">AUTO</span>
        )}
      </div>
      {!movingOpen ? (
        <button type="button" onClick={() => setMovingOpen(true)}
                    className="text-[10px] uppercase tracking-widest font-semibold text-primary hover:underline mt-1"
                    disabled={busy}
                    data-testid={`gtm-stage-card-move-${item.id}`}>
          Move →
        </button>
      ) : (
        <div className="flex flex-wrap gap-1 mt-1">
          {STAGES.filter(s => s.key !== currentStage).map(s => (
            <button key={s.key} type="button"
                       onClick={() => { onMove(s.key); setMovingOpen(false); }}
                       className="text-[9px] uppercase tracking-widest font-semibold border border-border px-1 hover:bg-primary hover:text-primary-foreground"
                       data-testid={`gtm-stage-card-move-${item.id}-to-${s.key}`}>
              {s.label}
            </button>
          ))}
          {item.overridden && (
            <button type="button"
                       onClick={() => { onMove(null); setMovingOpen(false); }}
                       className="text-[9px] uppercase tracking-widest font-semibold border border-destructive text-destructive px-1"
                       data-testid={`gtm-stage-card-clear-${item.id}`}>
              Clear
            </button>
          )}
          <button type="button" onClick={() => setMovingOpen(false)}
                     className="text-[9px] text-muted-foreground px-1">
            ×
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────── Apply GTM to Marketing (one-click) ───────────────
function ApplyToMarketingButton({ wsId, bid, pid }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    if (busy) return;
    if (!window.confirm("Auto-draft 4 marketing segments + 4 shell sequences (one per funnel stage) using your positioning pillars as message hooks?")) return;
    setBusy(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/brands/${bid}/gtm/playbooks/${pid}/apply-to-marketing`);
      setResult(r.data);
      const created = (r.data.created?.segments?.length || 0) + (r.data.created?.sequences?.length || 0);
      const reused = (r.data.reused?.segments?.length || 0) + (r.data.reused?.sequences?.length || 0);
      toast.success(`Applied · ${created} created${reused ? `, ${reused} reused` : ""}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Apply failed")); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" className="rounded-none h-7 bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={run} disabled={busy}
                data-testid="gtm-apply-to-marketing">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Zap className="w-3.5 h-3.5 mr-1" />}
        Apply to Marketing
      </Button>
      {result && (
        <Dialog open onOpenChange={(v) => { if (!v) setResult(null); }}>
          <DialogContent className="max-w-lg" data-testid="gtm-apply-result">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-emerald-600" /> Applied GTM → Marketing
              </DialogTitle>
            </DialogHeader>
            <div className="text-xs text-foreground/80">
              Used <b>{result.pillars_used}</b> positioning pillars as hooks.
              Go to Marketing → Segments / Sequences to review, then add
              template steps.
            </div>
            <div className="border border-emerald-300 bg-emerald-50/60 p-3 space-y-2">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-emerald-800">
                Created ({(result.created?.segments?.length || 0) + (result.created?.sequences?.length || 0)})
              </div>
              {(result.created?.segments || []).map(s => (
                <div key={s.id} className="text-xs" data-testid={`gtm-apply-created-seg-${s.id}`}>
                  📊 Segment · {s.name}
                </div>
              ))}
              {(result.created?.sequences || []).map(s => (
                <div key={s.id} className="text-xs" data-testid={`gtm-apply-created-seq-${s.id}`}>
                  ⚡ Sequence · {s.name} · {s.channel}
                </div>
              ))}
            </div>
            {((result.reused?.segments?.length || 0) + (result.reused?.sequences?.length || 0)) > 0 && (
              <div className="border border-border bg-muted/30 p-3 text-xs">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-1">
                  Reused (idempotent)
                </div>
                {[...(result.reused?.segments || []), ...(result.reused?.sequences || [])].map(x => (
                  <div key={x.id} className="text-xs">↻ {x.name}</div>
                ))}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" className="rounded-none"
                        onClick={() => setResult(null)}>Close</Button>
              <Link to="/app/marketing?tab=segments" onClick={() => setResult(null)}>
                <Button className="rounded-none" data-testid="gtm-apply-open-marketing">
                  Open Marketing <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </Link>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

// ─────────────── Explain this funnel (AI) ───────────────
function ExplainFunnelButton({ wsId, bid, pid }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/brands/${bid}/gtm/playbooks/${pid}/funnel/explain`);
      setResult(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Explain failed")); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" variant="outline" className="rounded-none h-7 border-primary text-primary"
                onClick={run} disabled={busy}
                data-testid="gtm-explain-funnel">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Lightbulb className="w-3.5 h-3.5 mr-1" />}
        Explain this funnel
      </Button>
      {result && (
        <Dialog open onOpenChange={(v) => { if (!v) setResult(null); }}>
          <DialogContent className="max-w-2xl" data-testid="gtm-explain-result">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-primary" /> Funnel health · AI review
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {result.headline && (
                <blockquote className="border-l-4 border-primary pl-4 py-2 text-base font-semibold">
                  {result.headline}
                </blockquote>
              )}
              {(result.insights || []).length > 0 && (
                <div className="space-y-2">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
                    Insights
                  </div>
                  {result.insights.map((i, idx) => (
                    <div key={idx} className="border border-border bg-card p-3" data-testid={`gtm-explain-insight-${idx}`}>
                      <div className="font-semibold text-sm">{i.title}</div>
                      <p className="text-xs text-muted-foreground mt-1">{i.body}</p>
                    </div>
                  ))}
                </div>
              )}
              {(result.next_actions || []).length > 0 && (
                <div className="space-y-2">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-emerald-700">
                    Next actions
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    {result.next_actions.map((a, idx) => (
                      <div key={idx} className="border border-emerald-400 bg-emerald-50/50 p-3 flex flex-col gap-1"
                              data-testid={`gtm-explain-action-${idx}`}>
                        <div className="text-sm font-bold">{a.title}</div>
                        <div className="text-[11px] text-emerald-900 italic">{a.why}</div>
                        <span className="mt-auto text-[10px] uppercase tracking-widest font-semibold border border-emerald-600 text-emerald-800 px-1 py-0.5 inline-block w-fit">
                          {a.cta}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button className="rounded-none" onClick={() => setResult(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

// ─────────────── Section: Launch Calendar ───────────────
const WAVE_STATUS_TONES = {
  planned:       "border-slate-400 bg-slate-50 text-slate-900",
  in_progress: "border-sky-500 bg-sky-50 text-sky-900",
  launched:    "border-emerald-500 bg-emerald-50 text-emerald-900",
  delayed:      "border-rose-500 bg-rose-50 text-rose-900",
};

function LaunchCalendarEditor({ wsId, bid, pid, doc, onUpdated }) {
  const [editing, setEditing] = useState(null);
  const waves = doc?.sections?.launch_calendar?.waves || [];
  const sorted = useMemo(() =>
    [...waves].sort((a, b) => (a.start_date || "").localeCompare(b.start_date || "")),
  [waves]);

  const gantt = useMemo(() => {
    if (!sorted.length) return null;
    const dates = sorted.flatMap(w => [w.start_date, w.end_date].filter(Boolean));
    if (!dates.length) return null;
    const min = new Date(dates.reduce((a, b) => a < b ? a : b));
    const max = new Date(dates.reduce((a, b) => a > b ? a : b));
    const spanDays = Math.max(1, Math.ceil((max - min) / (1000 * 60 * 60 * 24)) + 1);
    return { min, max, spanDays };
  }, [sorted]);

  const rmWave = async (id) => {
    if (!window.confirm("Delete wave?")) return;
    try {
      await api.delete(`/workspaces/${wsId}/brands/${bid}/gtm/playbooks/${pid}/launch-calendar/waves/${id}`);
      toast.success("Wave deleted");
      onUpdated?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const linkProject = async (wave) => {
    try {
      const r = await api.post(`/workspaces/${wsId}/brands/${bid}/gtm/playbooks/${pid}/launch-calendar/waves/${wave.id}/link-project`);
      if (r.data.already_linked) toast.info("Wave already linked to a project");
      else toast.success("Project scaffold created & linked");
      onUpdated?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Link failed")); }
  };

  return (
    <div className="space-y-4" data-testid="gtm-section-launch">
      <div className="border border-primary/30 bg-primary/5 p-3 flex items-center gap-2 flex-wrap">
        <Calendar className="w-4 h-4 text-primary" />
        <div className="text-xs text-foreground/80 flex-1 min-w-[240px]">
          <b>Launch waves.</b> Each wave can be linked to a Project — inheriting stakeholder + escalation matrices from your Projects module.
        </div>
        <Button size="sm" className="rounded-none h-7" onClick={() => setEditing({})}
                  data-testid="gtm-wave-new">
          <Plus className="w-3.5 h-3.5 mr-1" /> New wave
        </Button>
      </div>

      {sorted.length === 0 && (
        <div className="border border-dashed border-border p-10 text-center" data-testid="gtm-waves-empty">
          <Calendar className="w-8 h-8 mx-auto text-primary mb-2" />
          <div className="font-semibold">No launch waves planned yet.</div>
          <p className="text-xs text-foreground/60 mt-1">Waves are the &quot;phased chunks&quot; of your GTM — pilot, launch, expansion, etc.</p>
          <Button className="rounded-none mt-4" onClick={() => setEditing({})}
                    data-testid="gtm-waves-empty-start">
            <Plus className="w-4 h-4 mr-1" /> Plan your first wave
          </Button>
        </div>
      )}

      {sorted.length > 0 && gantt && (
        <div className="border border-border bg-card p-3 space-y-1 overflow-x-auto" data-testid="gtm-gantt">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
            Gantt · {gantt.min.toDateString()} → {gantt.max.toDateString()} ({gantt.spanDays}d)
          </div>
          {sorted.map(w => {
            const start = w.start_date ? new Date(w.start_date) : gantt.min;
            const end = w.end_date ? new Date(w.end_date) : start;
            const offsetPct = ((start - gantt.min) / (1000 * 60 * 60 * 24)) / gantt.spanDays * 100;
            const widthPct = Math.max(2, ((end - start) / (1000 * 60 * 60 * 24) + 1) / gantt.spanDays * 100);
            const tone = WAVE_STATUS_TONES[w.status] || WAVE_STATUS_TONES.planned;
            return (
              <div key={w.id} className="flex items-center gap-2 min-h-6" data-testid={`gtm-gantt-row-${w.id}`}>
                <div className="w-40 shrink-0 text-xs font-semibold truncate">{w.name}</div>
                <div className="flex-1 relative min-w-[300px] h-6 bg-muted/30">
                  <div className={`absolute h-6 border-2 ${tone} px-1 flex items-center gap-1 overflow-hidden text-[10px]`}
                          style={{ left: `${offsetPct}%`, width: `${widthPct}%` }}
                          title={`${w.status} · ${w.stage_focus || 'no stage'}`}>
                    <span className="uppercase tracking-widest font-bold">{w.status}</span>
                    {w.stage_focus && <span className="opacity-70">{w.stage_focus}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {sorted.map(w => (
          <div key={w.id} className={`border-2 ${(WAVE_STATUS_TONES[w.status] || "").split(" ")[0]} bg-card p-3 space-y-2`}
                  data-testid={`gtm-wave-card-${w.id}`}>
            <div className="flex items-center gap-2">
              <div className="font-semibold truncate flex-1">{w.name}</div>
              <span className={`text-[9px] uppercase tracking-widest font-bold border px-1.5 py-0.5 ${WAVE_STATUS_TONES[w.status] || ""}`}>
                {w.status}
              </span>
            </div>
            <div className="text-[11px] text-muted-foreground">
              {w.start_date || "—"} → {w.end_date || "—"}
              {w.stage_focus && ` · ${w.stage_focus}`}
              {w.owner_name && ` · ${w.owner_name}`}
            </div>
            {w.notes && <p className="text-xs text-foreground/70 line-clamp-2">{w.notes}</p>}
            {(w.milestones || []).length > 0 && (
              <div className="space-y-1">
                <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold">Milestones</div>
                {(w.milestones || []).slice(0, 4).map(m => (
                  <div key={m.id} className="text-[11px] flex items-center gap-1">
                    <span className={`w-2.5 h-2.5 border ${m.done ? "bg-emerald-500 border-emerald-500" : "border-border"}`} />
                    <span className={m.done ? "line-through text-muted-foreground" : ""}>{m.name}</span>
                    {m.due_date && <span className="text-muted-foreground ml-auto">{m.due_date}</span>}
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-1 pt-2 border-t border-border">
              <Button size="sm" variant="outline" className="rounded-none h-7"
                        onClick={() => setEditing(w)}
                        data-testid={`gtm-wave-edit-${w.id}`}>
                Edit
              </Button>
              {w.project_id ? (
                <Link to={`/app/projects/${w.project_id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-primary hover:underline border border-primary px-2 h-7"
                        data-testid={`gtm-wave-project-${w.id}`}>
                  <LinkIcon className="w-3 h-3" /> Open Project
                </Link>
              ) : (
                <Button size="sm" variant="outline" className="rounded-none h-7"
                          onClick={() => linkProject(w)}
                          data-testid={`gtm-wave-link-project-${w.id}`}>
                  <LinkIcon className="w-3 h-3 mr-1" /> Link Project
                </Button>
              )}
              <ApplyWaveToBucketButton wsId={wsId} bid={bid} pid={pid} wave={w}
                                                        onDone={onUpdated} />
              <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 ml-auto text-destructive"
                        onClick={() => rmWave(w.id)}
                        data-testid={`gtm-wave-delete-${w.id}`}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <WaveDialog wsId={wsId} bid={bid} pid={pid} wave={editing}
                          onClose={() => setEditing(null)}
                          onSaved={() => { setEditing(null); onUpdated?.(); }} />
      )}
    </div>
  );
}

function ApplyWaveToBucketButton({ wsId, bid, pid, wave, onDone }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    if (busy) return;
    if (!window.confirm(`Auto-draft ${wave.stage_focus ? "3-5 " + wave.stage_focus + "-stage" : "5"} content-bucket items for wave "${wave.name}"? Uses your positioning pillars as caption hooks.`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/brands/${bid}/gtm/playbooks/${pid}/launch-calendar/waves/${wave.id}/apply-to-content-bucket`);
      setResult(r.data);
      if (r.data.already_generated) toast.info(`Already drafted ${r.data.existing_count} items — reused`);
      else toast.success(`Drafted ${r.data.created?.length || 0} items`);
      onDone?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Draft failed")); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" variant="outline" className="rounded-none h-7 border-amber-500 text-amber-800"
                onClick={run} disabled={busy}
                data-testid={`gtm-wave-apply-bucket-${wave.id}`}>
        {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <FileText className="w-3 h-3 mr-1" />}
        Draft Content
      </Button>
      {result && (
        <Dialog open onOpenChange={v => { if (!v) setResult(null); }}>
          <DialogContent className="max-w-lg" data-testid="gtm-wave-bucket-result">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-amber-600" /> Content bucket drafted
              </DialogTitle>
            </DialogHeader>
            <div className="text-xs text-foreground/80">
              Wave <b>{wave.name}</b> · stage focus <b>{result.stage || wave.stage_focus || "—"}</b>
              {result.pillars_used > 0 && ` · anchored on ${result.pillars_used} positioning pillars`}.
            </div>
            {result.already_generated ? (
              <div className="border border-border bg-muted/30 p-3 text-xs italic">
                Already drafted <b>{result.existing_count}</b> items for this wave earlier — no duplicates were created.
              </div>
            ) : (
              <div className="border border-amber-300 bg-amber-50 p-3 space-y-1">
                {(result.created || []).map(c => (
                  <div key={c.id} className="text-xs" data-testid={`gtm-bucket-created-${c.id}`}>
                    📄 <b>{c.format}</b> · {c.topic_title}
                  </div>
                ))}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setResult(null)}>Close</Button>
              <Link to={`/app/brands/${bid}?tab=content-bucket`} onClick={() => setResult(null)}>
                <Button className="rounded-none" data-testid="gtm-bucket-open">
                  Open Content Bucket <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </Link>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

function WaveDialog({ wsId, bid, pid, wave, onClose, onSaved }) {
  const isNew = !wave.id;
  const [draft, setDraft] = useState({
    name: wave.name || "",
    start_date: wave.start_date || "",
    end_date: wave.end_date || "",
    stage_focus: wave.stage_focus || "",
    status: wave.status || "planned",
    owner_name: wave.owner_name || "",
    notes: wave.notes || "",
    milestones: wave.milestones || [],
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const setMilestone = (idx, k, v) => setDraft(d => ({
    ...d, milestones: d.milestones.map((m, i) => i === idx ? { ...m, [k]: v } : m)
  }));
  const addMilestone = () => setDraft(d => ({
    ...d, milestones: [...(d.milestones || []), { name: "", due_date: "", done: false }]
  }));
  const rmMilestone = (idx) => setDraft(d => ({
    ...d, milestones: d.milestones.filter((_, i) => i !== idx)
  }));

  const save = async () => {
    if (!draft.name.trim()) { toast.error("Name is required"); return; }
    if (draft.start_date && draft.end_date && draft.start_date > draft.end_date) {
      toast.error("End date must be after start date"); return;
    }
    setBusy(true);
    try {
      const payload = {
        ...draft,
        stage_focus: draft.stage_focus || null,
        owner_id: null,
        project_id: wave.project_id || null,
      };
      if (isNew) {
        await api.post(`/workspaces/${wsId}/brands/${bid}/gtm/playbooks/${pid}/launch-calendar/waves`, payload);
        toast.success("Wave created");
      } else {
        await api.patch(`/workspaces/${wsId}/brands/${bid}/gtm/playbooks/${pid}/launch-calendar/waves/${wave.id}`, payload);
        toast.success("Wave updated");
      }
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="gtm-wave-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-primary" /> {isNew ? "New wave" : `Edit · ${wave.name}`}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <TextField label="Wave name *" value={draft.name}
                        onChange={v => set("name", v)} testid="wave-name" />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Start date</Label>
              <Input type="date" value={draft.start_date}
                        onChange={e => set("start_date", e.target.value)}
                        className="rounded-none h-9" data-testid="gtm-wave-start" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">End date</Label>
              <Input type="date" value={draft.end_date}
                        onChange={e => set("end_date", e.target.value)}
                        className="rounded-none h-9" data-testid="gtm-wave-end" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Stage focus</Label>
              <Select value={draft.stage_focus || "__none__"}
                         onValueChange={v => set("stage_focus", v === "__none__" ? "" : v)}>
                <SelectTrigger className="h-9 rounded-none" data-testid="gtm-wave-stage">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  <SelectItem value="awareness">Awareness</SelectItem>
                  <SelectItem value="consideration">Consideration</SelectItem>
                  <SelectItem value="decision">Decision</SelectItem>
                  <SelectItem value="retention">Retention</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Status</Label>
              <Select value={draft.status} onValueChange={v => set("status", v)}>
                <SelectTrigger className="h-9 rounded-none" data-testid="gtm-wave-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned">Planned</SelectItem>
                  <SelectItem value="in_progress">In progress</SelectItem>
                  <SelectItem value="launched">Launched</SelectItem>
                  <SelectItem value="delayed">Delayed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <TextField label="Owner name" value={draft.owner_name}
                        onChange={v => set("owner_name", v)} testid="wave-owner" />
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Notes</Label>
            <Textarea rows={2} value={draft.notes}
                          onChange={e => set("notes", e.target.value)}
                          className="rounded-none text-sm"
                          data-testid="gtm-wave-notes" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Label className="label-eyebrow inline-block">Milestones</Label>
              <Button size="sm" variant="ghost" className="rounded-none h-6 px-2 ml-auto"
                        onClick={addMilestone}
                        data-testid="gtm-wave-milestone-add">
                <Plus className="w-3 h-3 mr-1" /> Add
              </Button>
            </div>
            <div className="space-y-1">
              {(draft.milestones || []).map((m, idx) => (
                <div key={idx} className="flex items-center gap-1" data-testid={`gtm-wave-milestone-${idx}`}>
                  <input type="checkbox" checked={m.done || false}
                            onChange={e => setMilestone(idx, "done", e.target.checked)} />
                  <Input value={m.name || ""}
                            onChange={e => setMilestone(idx, "name", e.target.value)}
                            placeholder="Milestone name"
                            className="h-8 rounded-none flex-1 text-sm"
                            data-testid={`gtm-wave-milestone-${idx}-name`} />
                  <Input type="date" value={m.due_date || ""}
                            onChange={e => setMilestone(idx, "due_date", e.target.value)}
                            className="h-8 rounded-none w-40 text-sm" />
                  <Button variant="ghost" size="sm" className="rounded-none h-8 px-2 text-destructive"
                            onClick={() => rmMilestone(idx)}
                            data-testid={`gtm-wave-milestone-remove-${idx}`}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy}
                    data-testid="gtm-wave-save">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
            {isNew ? "Create wave" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────── Section: Offer & Pricing ───────────────
function OfferPricingEditor({ wsId, bid, pid, value, onUpdated }) {
  const [draft, setDraft] = useState(value || { packages: [], promo_mechanics: [], pricing_notes: "" });
  const [aiPrompt, setAiPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDraft(value || { packages: [], promo_mechanics: [], pricing_notes: "" }); }, [value]);

  const setPkg = (idx, k, v) => setDraft(d => ({
    ...d, packages: d.packages.map((p, i) => i === idx ? { ...p, [k]: v } : p)
  }));
  const setPkgList = (idx, k, v) => setPkg(idx, k, v.split("\n").map(x => x.trim()).filter(Boolean));
  const setPopular = (idx) => setDraft(d => ({
    ...d, packages: d.packages.map((p, i) => ({ ...p, popular: i === idx }))
  }));
  const addPkg = () => setDraft(d => ({
    ...d, packages: [...(d.packages || []), { name: "", price: "", price_unit: "per month", features: [], target_persona: "", popular: false }]
  }));
  const rmPkg = (idx) => setDraft(d => ({ ...d, packages: d.packages.filter((_, i) => i !== idx) }));

  const setPromo = (idx, k, v) => setDraft(d => ({
    ...d, promo_mechanics: (d.promo_mechanics || []).map((p, i) => i === idx ? { ...p, [k]: v } : p)
  }));
  const addPromo = () => setDraft(d => ({
    ...d, promo_mechanics: [...(d.promo_mechanics || []), { name: "", terms: "", valid_from: "", valid_to: "" }]
  }));
  const rmPromo = (idx) => setDraft(d => ({
    ...d, promo_mechanics: (d.promo_mechanics || []).filter((_, i) => i !== idx)
  }));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/workspaces/${wsId}/brands/${bid}/gtm/playbooks/${pid}/sections/offer_pricing`, { value: draft });
      toast.success("Saved · revision recorded");
      onUpdated();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const aiGenerate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/brands/${bid}/gtm/playbooks/${pid}/sections/offer_pricing/ai-generate`, { prompt_extra: aiPrompt.trim() || null });
      setDraft(r.data.value);
      toast.success("Claude drafted the pricing plan — edit and save");
      onUpdated();
    } catch (e) { toast.error(apiErrorMessage(e, "AI failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="gtm-section-offer">
      <AiBar prompt={aiPrompt} setPrompt={setAiPrompt} onRun={aiGenerate} busy={busy}
                placeholder="Optional steer — e.g. 'Aggressive land-and-expand for Indian mid-market'"
                testid="offer" />
      <div className="space-y-3">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Packages</div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {(draft.packages || []).map((p, idx) => (
            <div key={idx} className={`border ${p.popular ? "border-emerald-500 shadow-md" : "border-border"} bg-card p-4 space-y-2 relative`}
                    data-testid={`gtm-package-${idx}`}>
              {p.popular && (
                <span className="absolute -top-2 left-3 bg-emerald-500 text-white text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 flex items-center gap-1">
                  <Star className="w-3 h-3" /> Popular
                </span>
              )}
              <TextField label="Package name" value={p.name || ""}
                            onChange={v => setPkg(idx, "name", v)}
                            testid={`pkg-${idx}-name`} />
              <div className="grid grid-cols-2 gap-2">
                <TextField label="Price" value={p.price || ""}
                              onChange={v => setPkg(idx, "price", v)}
                              testid={`pkg-${idx}-price`} />
                <div>
                  <Label className="label-eyebrow mb-1 inline-block">Price unit</Label>
                  <Select value={p.price_unit || "per month"} onValueChange={v => setPkg(idx, "price_unit", v)}>
                    <SelectTrigger className="h-9 rounded-none" data-testid={`gtm-input-pkg-${idx}-unit`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="per month">per month</SelectItem>
                      <SelectItem value="per year">per year</SelectItem>
                      <SelectItem value="one-time">one-time</SelectItem>
                      <SelectItem value="usage-based">usage-based</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <TextField label="Target persona" value={p.target_persona || ""}
                            onChange={v => setPkg(idx, "target_persona", v)}
                            testid={`pkg-${idx}-persona`} />
              <ListField label="Features (one per line)"
                            value={(p.features || []).join("\n")}
                            onChange={v => setPkgList(idx, "features", v)}
                            testid={`pkg-${idx}-features`} />
              <div className="flex items-center gap-1 pt-2 border-t border-border">
                <Button size="sm" variant={p.popular ? "default" : "outline"}
                          className={`rounded-none h-7 text-[10px] ${p.popular ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`}
                          onClick={() => setPopular(idx)}
                          data-testid={`gtm-pkg-popular-${idx}`}>
                  <Star className="w-3 h-3 mr-1" /> {p.popular ? "Most popular" : "Mark popular"}
                </Button>
                <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 ml-auto text-destructive"
                          onClick={() => rmPkg(idx)}
                          data-testid={`gtm-pkg-remove-${idx}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" className="rounded-none w-full"
                  onClick={addPkg} data-testid="gtm-pkg-add">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add package
        </Button>
      </div>

      <div className="space-y-3">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Promo mechanics</div>
        {(draft.promo_mechanics || []).map((p, idx) => (
          <div key={idx} className="border border-border bg-card p-4 grid grid-cols-1 md:grid-cols-4 gap-2 items-start"
                  data-testid={`gtm-promo-${idx}`}>
            <TextField label="Name" value={p.name || ""} onChange={v => setPromo(idx, "name", v)} testid={`promo-${idx}-name`} />
            <TextField label="Terms" value={p.terms || ""} onChange={v => setPromo(idx, "terms", v)} testid={`promo-${idx}-terms`} />
            <TextField label="Valid from" value={p.valid_from || ""} onChange={v => setPromo(idx, "valid_from", v)} testid={`promo-${idx}-from`} />
            <div className="flex items-start gap-1">
              <div className="flex-1">
                <TextField label="Valid to" value={p.valid_to || ""} onChange={v => setPromo(idx, "valid_to", v)} testid={`promo-${idx}-to`} />
              </div>
              <Button variant="ghost" size="sm" className="rounded-none h-9 px-1 text-destructive"
                        onClick={() => rmPromo(idx)} data-testid={`gtm-promo-remove-${idx}`}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        ))}
        <Button variant="outline" size="sm" className="rounded-none w-full"
                  onClick={addPromo} data-testid="gtm-promo-add">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add promo
        </Button>
      </div>

      <ListField label="Pricing notes" value={draft.pricing_notes || ""}
                    onChange={v => setDraft(d => ({ ...d, pricing_notes: v }))}
                    testid="pricing-notes" />

      <SaveBar onSave={save} busy={busy} testid="offer" />
    </div>
  );
}

// ─────────────── Section: Sales Enablement ───────────────
function SalesEnablementEditor({ wsId, bid, pid, value, onUpdated }) {
  const [draft, setDraft] = useState(value || { deck_url: "", one_pager_url: "", discovery_questions: [], battle_cards: [], objections: [] });
  const [aiPrompt, setAiPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDraft(value || { deck_url: "", one_pager_url: "", discovery_questions: [], battle_cards: [], objections: [] }); }, [value]);

  const setBc = (idx, k, v) => setDraft(d => ({
    ...d, battle_cards: (d.battle_cards || []).map((c, i) => i === idx ? { ...c, [k]: v } : c)
  }));
  const setBcList = (idx, k, v) => setBc(idx, k, v.split("\n").map(x => x.trim()).filter(Boolean));
  const setBcComeback = (idx, cbIdx, k, v) => setBc(idx, "best_comebacks",
    (draft.battle_cards?.[idx]?.best_comebacks || []).map((c, i) => i === cbIdx ? { ...c, [k]: v } : c));
  const addBcComeback = (idx) => setBc(idx, "best_comebacks",
    [...(draft.battle_cards?.[idx]?.best_comebacks || []), { they_say: "", we_say: "" }]);
  const rmBcComeback = (idx, cbIdx) => setBc(idx, "best_comebacks",
    (draft.battle_cards?.[idx]?.best_comebacks || []).filter((_, i) => i !== cbIdx));
  const addBc = () => setDraft(d => ({
    ...d, battle_cards: [...(d.battle_cards || []), { competitor: "", tagline: "", we_win_when: [], they_win_when: [], best_comebacks: [] }]
  }));
  const rmBc = (idx) => setDraft(d => ({ ...d, battle_cards: (d.battle_cards || []).filter((_, i) => i !== idx) }));

  const setObj = (idx, k, v) => setDraft(d => ({
    ...d, objections: (d.objections || []).map((o, i) => i === idx ? { ...o, [k]: v } : o)
  }));
  const addObj = () => setDraft(d => ({
    ...d, objections: [...(d.objections || []), { objection: "", response: "", rebuttal_proof: "" }]
  }));
  const rmObj = (idx) => setDraft(d => ({ ...d, objections: (d.objections || []).filter((_, i) => i !== idx) }));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/workspaces/${wsId}/brands/${bid}/gtm/playbooks/${pid}/sections/sales_enablement`, { value: draft });
      toast.success("Saved · revision recorded");
      onUpdated();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  const aiGenerate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/brands/${bid}/gtm/playbooks/${pid}/sections/sales_enablement/ai-generate`, { prompt_extra: aiPrompt.trim() || null });
      // Preserve user-entered deck & one-pager URLs — AI shouldn't clobber them.
      setDraft(prev => ({
        ...r.data.value,
        deck_url: prev.deck_url || r.data.value?.deck_url || "",
        one_pager_url: prev.one_pager_url || r.data.value?.one_pager_url || "",
      }));
      toast.success("Enablement kit drafted — add your deck link and save");
      onUpdated();
    } catch (e) { toast.error(apiErrorMessage(e, "AI failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="gtm-section-enablement">
      <AiBar prompt={aiPrompt} setPrompt={setAiPrompt} onRun={aiGenerate} busy={busy}
                placeholder="Optional steer — e.g. 'Focus on FinTech competitors: Zerodha, Groww'"
                testid="enab" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <TextField label="Deck URL" value={draft.deck_url || ""}
                      onChange={v => setDraft(d => ({ ...d, deck_url: v }))}
                      testid="enab-deck" />
        <TextField label="One-pager URL" value={draft.one_pager_url || ""}
                      onChange={v => setDraft(d => ({ ...d, one_pager_url: v }))}
                      testid="enab-onepager" />
      </div>

      <ListField label="Discovery questions (one per line)"
                    value={(draft.discovery_questions || []).join("\n")}
                    onChange={v => setDraft(d => ({ ...d, discovery_questions: v.split("\n").map(x => x.trim()).filter(Boolean) }))}
                    testid="enab-discovery" />

      <div className="space-y-3">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Battle cards</div>
        {(draft.battle_cards || []).map((c, idx) => (
          <div key={idx} className="border border-border bg-card p-4 space-y-2" data-testid={`gtm-battlecard-${idx}`}>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5 text-primary" />
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Battle card {idx + 1}</div>
              <Button variant="ghost" size="sm" className="rounded-none h-6 px-1 ml-auto text-destructive"
                        onClick={() => rmBc(idx)}
                        data-testid={`gtm-battlecard-remove-${idx}`}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <TextField label="Competitor" value={c.competitor || ""}
                            onChange={v => setBc(idx, "competitor", v)}
                            testid={`bc-${idx}-competitor`} />
              <TextField label="Tagline" value={c.tagline || ""}
                            onChange={v => setBc(idx, "tagline", v)}
                            testid={`bc-${idx}-tagline`} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <ListField label="We win when" value={(c.we_win_when || []).join("\n")}
                            onChange={v => setBcList(idx, "we_win_when", v)}
                            testid={`bc-${idx}-wewin`} />
              <ListField label="They win when" value={(c.they_win_when || []).join("\n")}
                            onChange={v => setBcList(idx, "they_win_when", v)}
                            testid={`bc-${idx}-theywin`} />
            </div>
            <div className="space-y-1">
              <Label className="label-eyebrow mb-1 inline-block">Best come-backs</Label>
              {(c.best_comebacks || []).map((cb, cbIdx) => (
                <div key={cbIdx} className="border border-border p-2 grid grid-cols-2 gap-1"
                        data-testid={`gtm-bc-comeback-${idx}-${cbIdx}`}>
                  <Input value={cb.they_say || ""} placeholder="They say…"
                            onChange={e => setBcComeback(idx, cbIdx, "they_say", e.target.value)}
                            className="rounded-none h-8 text-xs"
                            data-testid={`gtm-input-bc-${idx}-say-${cbIdx}`} />
                  <div className="flex items-center gap-1">
                    <Input value={cb.we_say || ""} placeholder="We say…"
                              onChange={e => setBcComeback(idx, cbIdx, "we_say", e.target.value)}
                              className="rounded-none h-8 text-xs flex-1"
                              data-testid={`gtm-input-bc-${idx}-response-${cbIdx}`} />
                    <Button variant="ghost" size="sm" className="rounded-none h-8 px-1 text-destructive"
                              onClick={() => rmBcComeback(idx, cbIdx)}>
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
              <Button variant="ghost" size="sm" className="rounded-none h-6 text-[10px]"
                        onClick={() => addBcComeback(idx)}
                        data-testid={`gtm-bc-add-comeback-${idx}`}>
                <Plus className="w-3 h-3 mr-1" /> Add come-back
              </Button>
            </div>
          </div>
        ))}
        <Button variant="outline" size="sm" className="rounded-none w-full"
                  onClick={addBc} data-testid="gtm-battlecard-add">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add battle card
        </Button>
      </div>

      <div className="space-y-3">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Objection library</div>
        {(draft.objections || []).map((o, idx) => (
          <div key={idx} className="border border-border bg-card p-4 space-y-2" data-testid={`gtm-objection-${idx}`}>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Objection {idx + 1}</span>
              <Button variant="ghost" size="sm" className="rounded-none h-6 px-1 ml-auto text-destructive"
                        onClick={() => rmObj(idx)} data-testid={`gtm-objection-remove-${idx}`}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
            <TextField label="Objection" value={o.objection || ""}
                          onChange={v => setObj(idx, "objection", v)} testid={`obj-${idx}-o`} />
            <ListField label="Response" value={o.response || ""}
                          onChange={v => setObj(idx, "response", v)} testid={`obj-${idx}-r`} />
            <TextField label="Rebuttal proof" value={o.rebuttal_proof || ""}
                          onChange={v => setObj(idx, "rebuttal_proof", v)} testid={`obj-${idx}-p`} />
          </div>
        ))}
        <Button variant="outline" size="sm" className="rounded-none w-full"
                  onClick={addObj} data-testid="gtm-objection-add">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add objection
        </Button>
      </div>

      <SaveBar onSave={save} busy={busy} testid="enab" />
    </div>
  );
}

// ─────────────── Shared form primitives ───────────────
function AiBar({ prompt, setPrompt, onRun, busy, placeholder, testid }) {
  return (
    <div className="border border-primary/40 bg-primary/5 p-3 flex items-center gap-2">
      <Wand2 className="w-4 h-4 text-primary shrink-0" />
      <Input value={prompt} onChange={e => setPrompt(e.target.value)}
                placeholder={placeholder}
                className="h-9 rounded-none flex-1"
                data-testid={`gtm-ai-prompt-${testid}`} />
      <Button size="sm" className="rounded-none" onClick={onRun} disabled={busy}
                data-testid={`gtm-ai-run-${testid}`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
        AI draft
      </Button>
    </div>
  );
}

function TextField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="label-eyebrow mb-1 inline-block">{label}</Label>
      <Input value={value} onChange={e => onChange(e.target.value)}
               className="h-9 rounded-none text-sm"
               data-testid={`gtm-input-${testid}`} />
    </div>
  );
}

function ListField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="label-eyebrow mb-1 inline-block">{label}</Label>
      <Textarea rows={3} value={value} onChange={e => onChange(e.target.value)}
                    className="rounded-none text-sm"
                    data-testid={`gtm-list-${testid}`} />
    </div>
  );
}

function SaveBar({ onSave, busy, testid }) {
  return (
    <div className="sticky bottom-0 z-10 bg-background/95 backdrop-blur border-t border-border pt-3 pb-2 flex items-center justify-end gap-2 -mx-1 px-1">
      <div className="text-[11px] text-muted-foreground mr-auto">
        Every save captures a revision — check History to compare or roll forward.
      </div>
      <Button className="rounded-none" onClick={onSave} disabled={busy}
                data-testid={`gtm-save-${testid}`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
        Save section
      </Button>
    </div>
  );
}

// ─────────────── Revisions drawer ───────────────
function RevisionsDrawer({ wsId, bid, pid, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/workspaces/${wsId}/brands/${bid}/gtm/playbooks/${pid}/revisions`)
      .then(r => setRows(r.data.rows || []))
      .catch(() => setRows([]));
  }, [wsId, bid, pid]);
  const sourceTone = (s) => ({
    ai: "border-violet-400 text-violet-800 bg-violet-50",
    manual: "border-emerald-400 text-emerald-800 bg-emerald-50",
    template: "border-amber-400 text-amber-800 bg-amber-50",
    duplicate: "border-blue-400 text-blue-800 bg-blue-50",
  })[s] || "border-border text-muted-foreground bg-muted/30";
  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="gtm-revisions-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-4 h-4 text-primary" /> Revision history
          </DialogTitle>
        </DialogHeader>
        {rows === null && <div className="text-sm text-foreground/60 p-4">Loading…</div>}
        {rows && rows.length === 0 && (
          <div className="text-sm text-muted-foreground italic p-4">No revisions yet.</div>
        )}
        {rows && rows.length > 0 && (
          <div className="max-h-96 overflow-y-auto divide-y divide-border">
            {rows.map(r => (
              <div key={r.id} className="p-3 text-xs" data-testid={`gtm-revision-${r.id}`}>
                <div className="flex items-center gap-2">
                  <span className={`text-[9px] font-semibold uppercase tracking-widest border px-1 ${sourceTone(r.source)}`}>
                    {r.source}
                  </span>
                  <span className="font-mono text-[10px]">{r.section}</span>
                  <span className="text-muted-foreground ml-auto">{fmtRelative(r.at)}</span>
                </div>
                <div className="text-[11px] mt-1"><b>{r.by_name || "—"}</b>{r.note ? ` · ${r.note}` : ""}</div>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
